<template>
  <div class="app-wrapper">
    <ToastNotification />
    <!-- Solo mostrar navegación si está autenticado -->
    <AppNavigation />
    <main
      class="main-content"
      :class="{
        'main-content-public': isPublicRoute,
        'main-content-authenticated':
          !isPublicRoute &&
          isAuthenticated &&
          hasSelectedTambo &&
          $route.path !== '/' &&
          $route.name !== 'tambos',
        'main-content-no-sidebar':
          !isPublicRoute &&
          isAuthenticated &&
          (!hasSelectedTambo ||
            $route.path === '/' ||
            $route.name === 'tambos'),
        'main-content-center': !isPublicRoute && !isAuthenticated,
        'main-content-mapa': isMapaRoute,
      }"
    >
      <router-view />
    </main>
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import AppNavigation from "./components/AppNavigation.vue";
import ToastNotification from "./components/ToastNotification.vue";

export default {
  components: { AppNavigation, ToastNotification },
  data() {
    return {
      // ============================================================
      // AUTO-REFRESH: Refresca datos periódicamente para mantenerlos
      // actualizados cuando múltiples usuarios editan al mismo tiempo
      // ============================================================
      autoRefreshTimer: null,
      lastRefreshTime: null,
      // Intervalo de auto-refresh en milisegundos (5 minutos)
      autoRefreshInterval: 5 * 60 * 1000,
      // Tiempo mínimo que la pestaña debe estar oculta para refrescar al volver (2 min)
      minHiddenTimeForRefresh: 2 * 60 * 1000,
      hiddenSince: null,

      // ============================================================
      // WEBSOCKETS REALTIME (Bretebot y otros)
      // ============================================================
      botWsConnection: null,
    };
  },

  methods: {
    ...mapActions("userTamboStore", ["autoLogin"]),
    ...mapActions("tambosStore", ["getTambos"]),
    ...mapActions("vacasStore", ["getVacas", "getPendientesCambioConCache"]),
    ...mapActions("lotesStore", ["getLotes"]),
    ...mapActions("despachosStore", ["getDespachos"]),
    ...mapActions("empresasStore", ["getEmpresas"]),
    ...mapActions("feedlotStore", [
      "getCorrales",
      "getAnimales",
      "getVistaCorrales",
      "getResumenTotal",
    ]),
    ...mapActions("offlineStore", {
      initOffline: "init",
      syncDeltaOffline: "syncDelta",
      syncFullOffline: "syncFull",
      flushAndSyncOffline: "flushAndSync",
    }),

    // ============================================================
    // AUTO-REFRESH
    // ============================================================

    /**
     * Inicia el sistema de auto-refresh que mantiene los datos actualizados.
     * Combina dos estrategias:
     * 1. Polling por intervalo (cada 5 min) cuando la pestaña está visible
     * 2. Refresh al volver a la pestaña si estuvo oculta más de 2 min
     */
    startAutoRefresh() {
      this.stopAutoRefresh();

      this.lastRefreshTime = Date.now();

      // 1. Polling por intervalo
      this.autoRefreshTimer = setInterval(() => {
        if (!document.hidden && this.isAuthenticated && this.hasSelectedTambo) {
          this.refreshCurrentViewData();
        }
      }, this.autoRefreshInterval);

      // 2. Listener de visibilidad (refrescar al volver a la pestaña)
      document.addEventListener(
        "visibilitychange",
        this.handleVisibilityChange
      );

      console.log(
        `🔄 Auto-refresh iniciado (cada ${
          this.autoRefreshInterval / 60000
        } min)`
      );
    },

    /**
     * Detiene el sistema de auto-refresh y limpia los listeners
     */
    stopAutoRefresh() {
      if (this.autoRefreshTimer) {
        clearInterval(this.autoRefreshTimer);
        this.autoRefreshTimer = null;
      }

      document.removeEventListener(
        "visibilitychange",
        this.handleVisibilityChange
      );
    },

    /**
     * Maneja el cambio de visibilidad de la pestaña.
     * Si la pestaña estuvo oculta más del tiempo mínimo, refresca los datos.
     */
    handleVisibilityChange() {
      if (document.hidden) {
        // La pestaña se ocultó - guardar timestamp
        this.hiddenSince = Date.now();
      } else {
        // La pestaña volvió a ser visible
        if (!this.isAuthenticated || !this.hasSelectedTambo) return;

        const tiempoOculta = this.hiddenSince
          ? Date.now() - this.hiddenSince
          : 0;

        if (tiempoOculta >= this.minHiddenTimeForRefresh) {
          console.log(
            `👁️ Pestaña visible nuevamente tras ${Math.round(
              tiempoOculta / 60000
            )} min - Refrescando datos...`
          );
          this.refreshCurrentViewData();
        }

        this.hiddenSince = null;
      }
    },

    /**
     * Refresca los datos relevantes según la vista actual del usuario.
     * Solo recarga los stores que usa la ruta activa para minimizar
     * la carga en el servidor.
     */
    async refreshCurrentViewData() {
      if (!this.isAuthenticated || !this.hasSelectedTambo || this.isPendiente) {
        return;
      }

      const rutaActual = this.$route.name;
      const ahora = Date.now();

      // Evitar refreshes muy seguidos (mínimo 30 segundos entre cada uno)
      if (this.lastRefreshTime && ahora - this.lastRefreshTime < 30000) {
        return;
      }

      this.lastRefreshTime = ahora;
      console.log(`🔄 Auto-refresh para vista: ${rutaActual}`);

      try {
        // Mapeo de ruta → acciones de refresh
        const refreshMap = {
          vacas: () =>
            Promise.all([
              this.getVacas({ pagina: 1, limite: 15 }),
              this.getPendientesCambioConCache({ pagina: 1, limite: 100 }),
              this.getLotes(),
            ]),
          despachos: () =>
            Promise.all([this.getDespachos(), this.getEmpresas()]),
          lotes: () => this.getLotes(),
          corralesFeedlot: () =>
            Promise.all([
              this.getCorrales(),
              this.getVistaCorrales(),
              this.getResumenTotal(),
            ]),
          engorde: () => Promise.all([this.getCorrales(), this.getAnimales()]),
        };

        const refreshFn = refreshMap[rutaActual];
        if (refreshFn) {
          await refreshFn();
          console.log(`✅ Auto-refresh completado para: ${rutaActual}`);
        }
      } catch (error) {
        // No notificar al usuario por errores de auto-refresh silencioso
        console.warn("⚠️ Error en auto-refresh:", error);
      }
    },

    // ============================================================
    // WEBSOCKETS REALTIME (Bretebot)
    // ============================================================

    connectRealtimeWebSocket() {
      if (!this.currentTamboId) return;
      this.disconnectRealtimeWebSocket();

      const apiUrl = process.env.VUE_APP_API_TAMBO || "http://localhost:8000";
      const wsUrl =
        apiUrl.replace(/^http/, "ws") +
        `/api/v1/realtime/ws/${this.currentTamboId}`;

      try {
        this.botWsConnection = new WebSocket(wsUrl);

        this.botWsConnection.onopen = () => {
          console.log("🟢 Conectado a Bretebot Realtime (WS)");
        };

        this.botWsConnection.onmessage = (event) => {
          try {
            const data = JSON.parse(event.data);
            if (data.event === "bot_action") {
              console.log("🤖 Bretebot registró una acción:", data.action);

              // Mostrar toast usando el plugin de notificaciones (si existe notify, o usar alert fallback)
              if (this.$notify) {
                this.$notify({
                  type: "success",
                  title: "Bretebot",
                  message:
                    "Se ha registrado un nuevo dato en el sistema. Actualizando...",
                });
              }

              // Disparar evento para componentes que requieran reactividad en tiempo real (ej. Recorridos de Engorde)
              window.dispatchEvent(
                new CustomEvent("bretebot-accion-registrada", { detail: data })
              );

              // Forzar recarga de datos en la vista actual
              this.refreshCurrentViewData();
            }
          } catch (e) {
            console.error("Error parseando WS event", e);
          }
        };

        this.botWsConnection.onclose = () => {
          console.log("🔴 Desconectado de Bretebot Realtime (WS)");
        };
      } catch (err) {
        console.warn("No se pudo establecer conexión WebSocket", err);
      }
    },

    disconnectRealtimeWebSocket() {
      if (this.botWsConnection) {
        this.botWsConnection.close();
        this.botWsConnection = null;
      }
    },

    handleAuthExpired() {
      // Si el usuario está navegando por una landing o ruta pública, no redirigir a login
      if (this.isPublicRoute) return;
      if (this.$route.name !== "login" && this.$route.path !== "/login") {
        this.$router.push("/login");
      }
    },
  },

  computed: {
    ...mapGetters("userTamboStore", [
      "isAuthenticated",
      "currentTamboId",
      "currentTamboName",
      "hasSelectedTambo",
      "currentRole",
      "isPendiente",
    ]),
    isPublicRoute() {
      return (
        this.$route.meta?.requiresAuth === false ||
        [
          "landing",
          "vaquiano",
          "baquiano",
          "tfp",
          "tfpDev",
          "tfpAgro",
          "login",
          "register",
          "terms",
          "suscripciones",
          "forgotPassword",
          "invitacion",
          "verify",
        ].includes(this.$route.name)
      );
    },
    isMapaRoute() {
      return (
        this.$route.name === "mapaAgronomico" ||
        this.$route.path.includes("mapa-agronomico")
      );
    },
  },

  async mounted() {
    window.addEventListener("auth:expired", this.handleAuthExpired);
    await this.$router.isReady();

    // Inicializar sistema offline (listeners online/offline + cargar pendientes)
    this.initOffline();

    const autoLoginSuccess = await this.autoLogin();

    if (autoLoginSuccess) {
      console.log("✅ Auto-login exitoso");

      await this.getTambos();

      if (this.hasSelectedTambo) {
        console.log(`📥 Cargando datos del tambo: ${this.currentTamboName}`);

        if (!this.isPendiente) {
          console.log(
            `✅ Usuario con rol: ${this.currentRole} - Cargando datos...`
          );
        } else {
          console.warn("⚠️ Usuario con rol PENDIENTE - No se cargan datos");
        }

        console.log("✅ Datos iniciales cargados");

        // Sincronizar datos para uso offline
        this.syncDeltaOffline().then((ok) => {
          if (ok) console.log("✅ Sync delta offline completado");
        });

        // Iniciar el auto-refresh de datos
        this.startAutoRefresh();

        // Iniciar WebSocket de realtime
        this.connectRealtimeWebSocket();
      } else {
        console.warn("⚠️ No hay tambo seleccionado");
        if (
          !this.isPublicRoute &&
          this.$route.name !== "tambos" &&
          this.$route.name !== "login"
        ) {
          this.$router.push("/");
        }
      }
    } else {
      console.log("⚠️ Auto-login falló");
      // Solo redirigir a login si la ruta actual no es pública y requiere autenticación
      if (!this.isPublicRoute && this.$route.meta?.requiresAuth !== false) {
        this.$router.push("/login");
      }
    }
  },

  beforeUnmount() {
    window.removeEventListener("auth:expired", this.handleAuthExpired);
    this.stopAutoRefresh();
    this.disconnectRealtimeWebSocket();
  },
};
</script>

<style>
@font-face {
  font-family: "Bank Gothic";
  src: url("@/assets/branding/BankGothic-Medium.ttf") format("truetype");
  font-weight: 500;
  font-style: normal;
}
@font-face {
  font-family: "Bank Gothic";
  src: url("@/assets/branding/BankGothic-Bold.ttf") format("truetype");
  font-weight: 700;
  font-style: normal;
}

*,
*::before,
*::after {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

:root {
  --bg-color-app: #f3f3f3;
  --bg-color-btnheader-active: #05224d;
  --navbar-height: 65px;
  --sidebar-width: 240px;
  --sidebar-width-mobile: 60px;
  --safe-area-top: env(safe-area-inset-top, 0px);
  --safe-area-bottom: env(safe-area-inset-bottom, 0px);
}

html {
  background-color: #123364;
}

body {
  height: 100%;
  background-color: var(--bg-color-app);
  overflow-x: hidden;
}

#app {
  font-family: Avenir, Helvetica, Arial, sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  text-align: center;
  color: #2c3e50;
  min-height: 100vh;
  background-color: var(--bg-color-app);
}

.app-wrapper {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
}

.main-content {
  flex-grow: 1;
  padding: 0;
  background-color: var(--bg-color-app);
  min-height: calc(100vh - var(--navbar-height));
}

.main-content-public {
  padding: 0;
  margin: 0;
  background: transparent;
  min-height: 100vh;
}

.main-content-authenticated {
  margin-top: calc(var(--navbar-height) + var(--safe-area-top));
  margin-left: var(--sidebar-width);
  padding: 0;
  transition: margin-left 0.3s ease;
}

.main-content-no-sidebar {
  margin-top: calc(var(--navbar-height) + var(--safe-area-top));
  margin-left: 0;
  padding: 0;
}

.main-content-center {
  margin-top: calc(var(--navbar-height) + var(--safe-area-top));
  margin-left: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: calc(100vh - var(--navbar-height) - var(--safe-area-top));
}

.main-content-mapa {
  padding: 0 !important;
  margin-bottom: 0 !important;
  padding-bottom: 0 !important;
  overflow: hidden !important;
  display: flex;
  flex-direction: column;
  height: calc(100vh - var(--navbar-height) - var(--safe-area-top));
  min-height: 0 !important;
}

/* ✅ Responsive para tablets */
@media (max-width: 768px) {
  .main-content-authenticated {
    margin-left: var(--sidebar-width-mobile);
    padding: 0;
  }
  .main-content-mapa {
    padding: 0 !important;
    padding-bottom: 0 !important;
    height: calc(100vh - var(--navbar-height) - var(--safe-area-top));
  }
}

/* ✅ Responsive para móviles */
@media (max-width: 480px) {
  :root {
    --sidebar-width-mobile: 55px;
  }

  .main-content-authenticated {
    margin-left: var(--sidebar-width-mobile);
    padding: 0;
    padding-bottom: calc(1rem + var(--safe-area-bottom));
  }

  .main-content-no-sidebar {
    padding: 0;
    padding-bottom: calc(1rem + var(--safe-area-bottom));
  }

  .main-content-mapa {
    padding: 0 !important;
    padding-bottom: 0 !important;
    height: calc(100vh - var(--navbar-height) - var(--safe-area-top));
  }
}

/* ✅ Estilos globales para mejor UX móvil */
@media (max-width: 768px) {
  /* Botones más grandes para touch */
  .btn {
    min-height: 44px;
  }

  /* Inputs más grandes */
  .form-control,
  .form-select {
    min-height: 44px;
    font-size: 16px !important; /* Previene zoom en iOS */
  }

  /* Modales mejor adaptados */
  .modal-dialog {
    margin: 0.5rem;
    max-height: calc(100vh - 1rem);
  }

  .modal-content {
    max-height: calc(100vh - 1rem);
  }

  .modal-body {
    overflow-y: auto;
    max-height: calc(100vh - 200px);
  }
}

/* ============================================================
   ESTILOS GLOBALES PARA TABLAS (Sin scope)
   ============================================================ */

/* ------ Alertas dentro de table-responsive ------ */
.table-responsive .alert {
  width: 100%;
  margin: 1rem 0;
  min-width: 100%; /* Forzar ancho completo del contenedor */
}

/* Cuando la alerta es el único elemento, forzar ancho mínimo */
.table-responsive:has(> .alert:only-child),
.table-responsive:has(> .alert:first-child:last-child) {
  min-width: 100%;
  width: 100%;
}

/* Fallback para navegadores sin :has() */
.table-responsive > .alert:first-child:last-child {
  min-width: calc(100vw - var(--sidebar-width) - 3rem);
}

/* Asegurar que las alertas se vean bien en tablas vacías */
.table-responsive > .alert:first-child {
  margin-top: 0;
}

.table-responsive > .alert:last-child {
  margin-bottom: 0;
}

/* Responsive para móviles */
@media (max-width: 768px) {
  .table-responsive > .alert:first-child:last-child {
    min-width: calc(100vw - var(--sidebar-width-mobile) - 1.5rem);
  }
}

/* ============================================================
   ESTILOS GLOBALES PARA MODALES (Sin scope)
   ============================================================ */

/* ------ Centrado vertical y horizontal de modales ------ */
.modal.show .modal-dialog {
  margin: auto !important;
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
  min-height: calc(
    100% - 1rem - var(--safe-area-top) - var(--safe-area-bottom)
  ) !important;
}

.modal-dialog {
  margin: calc(0.5rem + var(--safe-area-top)) auto
    calc(0.5rem + var(--safe-area-bottom)) auto !important;
}

.modal-body {
  padding: 1rem;
  overflow-y: auto;
}

.modal-content {
  border-radius: 0.5rem;
  box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.15);
}

/* ------ Responsive para tablets (max-width: 768px) ------ */
@media (max-width: 768px) {
  .modal-dialog {
    max-width: 100%;
    margin: calc(0.25rem + var(--safe-area-top)) 0.25rem
      calc(0.25rem + var(--safe-area-bottom)) 0.25rem !important;
  }

  .modal-lg,
  .modal-xl {
    max-width: calc(100% - 0.5rem);
    margin: calc(0.5rem + var(--safe-area-top)) auto
      calc(0.5rem + var(--safe-area-bottom)) auto !important;
  }

  .modal-body {
    padding: 0.75rem;
  }

  .modal-footer {
    padding: 0.75rem;
  }
}

/* ------ Responsive para móviles (max-width: 576px) ------ */
@media (max-width: 576px) {
  .modal.show .modal-dialog {
    min-height: calc(
      100% - 0.5rem - var(--safe-area-top) - var(--safe-area-bottom)
    ) !important;
  }

  .modal-dialog {
    margin: calc(0.25rem + var(--safe-area-top)) auto
      calc(0.25rem + var(--safe-area-bottom)) auto !important;
    max-width: calc(100% - 0.5rem);
  }

  .modal-lg,
  .modal-xl {
    max-width: calc(100% - 0.5rem);
    margin: calc(0.25rem + var(--safe-area-top)) auto
      calc(0.25rem + var(--safe-area-bottom)) auto !important;
  }

  .modal-body {
    padding: 0.5rem;
  }

  .modal-header {
    padding: 0.75rem 0.5rem;
  }

  .modal-header .modal-title {
    font-size: 1rem;
  }

  .modal-footer {
    padding: 0.5rem;
    flex-direction: column;
    gap: 0.5rem;
  }

  .modal-footer .btn {
    width: 100%;
    margin: 0 !important;
  }
}

/* ------ Safe area support para modales en dispositivos móviles ------ */
@supports (padding: max(0px)) {
  .modal-dialog {
    margin-top: max(0.5rem, env(safe-area-inset-top)) !important;
    margin-bottom: max(0.5rem, env(safe-area-inset-bottom)) !important;
  }

  @media (max-width: 768px) {
    .modal-dialog {
      margin-top: max(0.25rem, env(safe-area-inset-top)) !important;
      margin-bottom: max(0.25rem, env(safe-area-inset-bottom)) !important;
    }
  }

  @media (max-width: 576px) {
    .modal-dialog {
      margin-top: max(0.25rem, env(safe-area-inset-top)) !important;
      margin-bottom: max(0.25rem, env(safe-area-inset-bottom)) !important;
    }
  }
}

@supports (padding-bottom: env(safe-area-inset-bottom)) {
  .modal-body {
    padding-bottom: calc(1rem + env(safe-area-inset-bottom));
  }

  .modal-footer {
    padding-bottom: calc(0.75rem + env(safe-area-inset-bottom));
  }

  @media (max-width: 576px) {
    .modal-footer {
      padding-bottom: calc(0.5rem + env(safe-area-inset-bottom));
    }
  }
}

/* ------ Prevenir que el backdrop cubra el notch ------ */
.modal {
  padding-top: var(--safe-area-top) !important;
  padding-bottom: var(--safe-area-bottom) !important;
}

/* ------ Mejoras de UX para dispositivos táctiles ------ */
@media (pointer: coarse) {
  .modal .btn {
    min-height: 44px;
    min-width: 44px;
    padding: 0.5rem 1rem;
  }

  .modal .btn-sm {
    min-height: 36px;
    min-width: 36px;
  }

  .modal .form-control,
  .modal .form-select {
    min-height: 44px;
    font-size: 16px !important; /* Previene zoom en iOS */
  }

  .modal .form-check-input {
    width: 1.25rem;
    height: 1.25rem;
  }
}

/* ------ Animaciones suaves para modales ------ */
.modal.fade .modal-dialog {
  transition: transform 0.3s ease-out;
}

.modal.show .modal-dialog {
  transform: none;
}

/* ------ Bottom-Sheet Real Anclado al Fondo en Mobile (< 768px) ------ */
@media (max-width: 767.98px) {
  .modal-bottom-sheet-mobile .modal-dialog,
  .modal.modal-bottom-sheet-mobile .modal-dialog,
  .modal-bottom-sheet-mobile.modal.show .modal-dialog,
  .modal.show.modal-bottom-sheet-mobile .modal-dialog,
  .dietas-container .modal-dialog {
    margin: 0 !important;
    margin-top: auto !important;
    position: fixed !important;
    bottom: 0 !important;
    left: 0 !important;
    right: 0 !important;
    width: 100% !important;
    max-width: 100% !important;
    min-height: 0 !important;
    display: flex !important;
    flex-direction: column !important;
    justify-content: flex-end !important;
    z-index: 1055 !important;
  }

  .modal-bottom-sheet-mobile .modal-content,
  .modal.modal-bottom-sheet-mobile .modal-content,
  .dietas-container .modal-content {
    border-radius: 1.25rem 1.25rem 0 0 !important;
    border-bottom: none !important;
    max-height: 90vh !important;
    display: flex !important;
    flex-direction: column !important;
    position: relative !important;
    padding-top: 0.25rem !important;
    box-shadow: 0 -10px 25px rgba(0, 0, 0, 0.15) !important;
  }

  .modal-bottom-sheet-mobile .modal-content::before,
  .dietas-container .modal-content::before {
    content: "";
    display: block;
    width: 36px;
    height: 4px;
    background-color: #cbd5e1;
    border-radius: 2px;
    margin: 8px auto 4px auto;
    flex-shrink: 0;
  }

  .modal-bottom-sheet-mobile .modal-body,
  .dietas-container .modal-body {
    overflow-y: auto !important;
    flex: 1 1 auto !important;
    -webkit-overflow-scrolling: touch;
  }

  .modal-bottom-sheet-mobile .modal-footer,
  .dietas-container .modal-footer {
    flex-shrink: 0 !important;
    background-color: #ffffff !important;
    border-top: 1px solid #e2e8f0 !important;
    padding: 0.75rem 1rem !important;
    width: 100% !important;
    display: flex !important;
    flex-direction: row !important;
    flex-wrap: wrap !important;
    gap: 0.5rem !important;
  }

  .modal-bottom-sheet-mobile.fade .modal-dialog,
  .dietas-container .modal.fade .modal-dialog {
    transition: transform 0.3s cubic-bezier(0.16, 1, 0.3, 1) !important;
    transform: translateY(100%) !important;
  }

  .modal-bottom-sheet-mobile.show .modal-dialog,
  .dietas-container .modal.show .modal-dialog {
    transform: translateY(0) !important;
  }
}

@supports (padding: max(0px)) {
  @media (max-width: 767.98px) {
    .modal-bottom-sheet-mobile .modal-dialog,
    .modal.modal-bottom-sheet-mobile .modal-dialog,
    .dietas-container .modal-dialog {
      margin: 0 !important;
      margin-top: auto !important;
      margin-bottom: 0 !important;
    }
  }
}

/* ------ Ocultar botón sidebar cuando hay modal abierta ------ */
body.modal-open .sidebar-toggle-btn {
  display: none !important;
}
</style>
